/**
 * DossierAuditSuccess — Page de retour après commande d'audit dossier.
 *
 * URLs supportées :
 *   /dossier/:caseId/audit/success?audit_id=…&session_id=cs_…    (paiement Stripe)
 *   /dossier/:caseId/audit/success?audit_id=…&admin_test=1        (bypass ADMIN_TEST)
 *
 * v3.45 — Fix parcours "écran vide après commande" :
 *   1. Sablier Confilex animé pendant toute la préparation
 *   2. Messages progressifs (préparation → analyse → finalisation)
 *   3. Reconnaît payment_status="ADMIN_TEST" en plus de "paid"
 *   4. Bouton "Réessayer sans re-payer" en cas d'erreur
 *   5. Timeout gracieux après 30s (au lieu du silence infini)
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import BookingNavbar from "@/components/booking/BookingNavbar";
import Footer from "@/components/Footer";
import Seo from "@/components/Seo";
import ConfilexHourglass from "@/components/ConfilexHourglass";
import { CheckCircle2, Send, ArrowRight, Sparkles, FileText } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_POLL_ATTEMPTS = 20; // ~30s
const POLL_INTERVAL_MS = 1500;

// Statuts terminaux qui indiquent que l'audit est prêt
const READY_STATUSES = new Set(["paid", "ADMIN_TEST"]);

export default function DossierAuditSuccess() {
  const { caseId } = useParams();
  const [params] = useSearchParams();
  const auditId = params.get("audit_id");
  const sessionId = params.get("session_id");
  const isAdminTest = params.get("admin_test") === "1";

  const [audit, setAudit] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const [error, setError] = useState(null);
  const [pollActive, setPollActive] = useState(true);

  const paid = audit && READY_STATUSES.has(audit.payment_status);
  const transmitted = audit?.transmission_status === "transmitted";
  const ready = paid && (transmitted || isAdminTest);

  const startPolling = useCallback(() => {
    setError(null);
    setAttempts(0);
    setPollActive(true);
  }, []);

  useEffect(() => {
    if (!caseId || !auditId) {
      setError("Paramètres de retour invalides. Vérifiez le lien reçu.");
      return;
    }
    if (!pollActive) return;

    let stopped = false;
    let tries = 0;

    const tick = async () => {
      try {
        const r = await axios.get(`${API}/dossier/${caseId}/audit/${auditId}`);
        if (stopped) return;
        setAudit(r.data || null);
        tries += 1;
        setAttempts(tries);
        // Terminé si paiement validé ET (transmission ok OU admin_test)
        if (r.data && READY_STATUSES.has(r.data.payment_status)) {
          if (r.data.transmission_status === "transmitted" || isAdminTest) {
            return; // finished
          }
        }
        if (tries < MAX_POLL_ATTEMPTS) {
          setTimeout(tick, POLL_INTERVAL_MS);
        } else {
          // Timeout gracieux
          setError(
            "Le traitement prend plus de temps que prévu. Votre commande est bien enregistrée — actualisez la page dans quelques instants pour voir le rapport.",
          );
        }
      } catch (e) {
        if (!stopped) {
          setError(e?.response?.data?.detail || "Erreur de chargement du statut de l'audit.");
        }
      }
    };
    tick();
    return () => { stopped = true; };
  }, [caseId, auditId, isAdminTest, pollActive]);

  // Étape courante pour le sablier (message progressif)
  const hourglassProps = useMemo(() => {
    if (attempts <= 3) {
      return {
        stage: "preparation",
        message: "Confilex prépare votre audit documentaire…",
        substeps: ["Réception de votre commande", "Validation du paiement"],
      };
    }
    if (attempts <= 10) {
      return {
        stage: "analysis",
        message: "Analyse de vos documents en cours…",
        substeps: [
          "Réception de votre commande",
          "Validation du paiement",
          "Analyse IA de la structure du dossier",
        ],
      };
    }
    return {
      stage: "finalization",
      message: "Votre rapport est en cours de finalisation…",
      substeps: [
        "Réception de votre commande",
        "Validation du paiement",
        "Analyse IA de la structure du dossier",
        "Rédaction du rapport final",
      ],
    };
  }, [attempts]);

  return (
    <div className="cb-shell" data-testid="dossier-audit-success">
      <Seo title="Audit Confilex confirmé · Confilex" description="Audit documentaire Confilex — préparation et suivi." />
      <BookingNavbar />

      <section className="max-w-3xl mx-auto px-5 lg:px-8 py-12">
        <div className="cb-card p-8 lg:p-10 space-y-6">

          {/* En-tête */}
          <div className="border-b border-[#063D25] pb-5">
            <p className="text-xs uppercase tracking-wider cb-gold font-bold">
              Audit Confilex préparatoire
            </p>
            <h1 className="cb-serif text-2xl sm:text-3xl font-semibold cb-text-ivory mt-1 leading-tight">
              {ready
                ? "Votre audit documentaire est prêt"
                : error
                  ? "Une difficulté est survenue"
                  : "Confilex prépare votre audit…"}
            </h1>
          </div>

          {/* PHASE 1 — Sablier de préparation */}
          {!ready && (
            <ConfilexHourglass
              {...hourglassProps}
              error={error}
              onRetry={error ? startPolling : null}
              size="md"
              testid="audit-hourglass"
            />
          )}

          {/* PHASE 2 — Résultat prêt */}
          {ready && (
            <>
              <div className="flex items-start gap-3 p-4 rounded-lg bg-[#063D25]/40 border border-[#2ECC71]/30" data-testid="audit-ready-banner">
                <CheckCircle2 className="w-6 h-6 text-[#2ECC71] shrink-0 mt-0.5" />
                <div className="min-w-0">
                  <p className="text-sm cb-text-ivory font-semibold">
                    Audit terminé — votre dossier est prêt
                  </p>
                  <p className="text-xs text-[#BFC8C2] mt-0.5">
                    {isAdminTest
                      ? "Mode ADMIN_TEST — audit gratuit généré à des fins de test interne."
                      : "Merci de votre confiance. Votre dossier structuré vient d'être transmis au spécialiste sélectionné."}
                  </p>
                </div>
              </div>

              {/* Tuiles récapitulatives */}
              {audit && (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
                  <Tile label="Niveau d'audit" value={audit.level || "—"} testid="audit-level-tile" />
                  <Tile label="Montant" value={audit.amount_eur != null ? `${audit.amount_eur} € TTC` : "—"} testid="audit-amount-tile" />
                  <Tile label="Statut" value={audit.payment_status || "—"} testid="audit-payment-status-tile" />
                </div>
              )}

              {/* Confirmation transmission */}
              {audit?.transmission_id && (
                <div className="p-4 rounded-lg bg-[#031B12] border border-[#063D25] flex items-start gap-3" data-testid="transmission-confirmed">
                  <Send className="w-5 h-5 cb-gold shrink-0 mt-0.5" />
                  <div className="min-w-0">
                    <p className="text-sm cb-text-ivory font-semibold">Dossier transmis avec succès</p>
                    <p className="text-xs text-[#BFC8C2] mt-0.5">
                      Identifiant transmission : <span className="font-mono">{audit.transmission_id}</span>
                    </p>
                  </div>
                </div>
              )}

              {/* Lien vers le rapport si disponible */}
              {audit?.report_url && (
                <a
                  href={audit.report_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="cb-btn-gold inline-flex items-center gap-2 text-sm w-full sm:w-auto justify-center"
                  data-testid="audit-report-link"
                >
                  <FileText className="w-4 h-4" /> Consulter mon rapport d'audit
                </a>
              )}
            </>
          )}

          {/* Boutons de sortie */}
          <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-[#063D25]">
            <Link
              to="/"
              className="cb-btn-ghost text-sm inline-flex items-center justify-center"
              data-testid="back-home"
            >
              Retour à l'accueil
            </Link>
            {ready && (
              <Link
                to="/recherche"
                className="cb-btn-gold text-sm inline-flex items-center justify-center gap-1.5"
                data-testid="new-search"
              >
                <Sparkles className="w-4 h-4" /> Explorer d'autres spécialistes <ArrowRight className="w-4 h-4" />
              </Link>
            )}
          </div>

          {sessionId && (
            <p className="text-[10px] text-[#BFC8C2]/60 font-mono break-all pt-2" data-testid="stripe-session-id">
              Session Stripe : {sessionId}
            </p>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}

function Tile({ label, value, testid }) {
  return (
    <div className="p-3 rounded-lg bg-[#031B12] border border-[#063D25]" data-testid={testid}>
      <p className="text-[10px] uppercase tracking-wider cb-gold/80 font-bold">{label}</p>
      <p className="text-sm cb-text-ivory font-semibold mt-1 capitalize">{value}</p>
    </div>
  );
}
